import axios, { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { ADMIN_API_URL } from "../../../utils/env";

export const LEGACY_REFERRAL_CLAIM_DEADLINE = 1794700800; // 15 November 2026, 00:00 UTC.

export interface LegacyReferralRetirementState {
  claim_deadline_unix: typeof LEGACY_REFERRAL_CLAIM_DEADLINE;
  target_account_type: "STANDARD";
  perpetual_taker_bps: "6.5";
  perpetual_maker_bps: "3.5";
  preflight_error: null | string;
  retirement: null | {
    retired_seq: number;
    retired_at_unix_nano: number;
    claim_deadline_unix: typeof LEGACY_REFERRAL_CLAIM_DEADLINE;
  };
  standard_perp_fee_reset: null | { seq: number; timestamp_unix_nano: number };
}

export const legacyReferralRetirementError = (error: unknown): string => {
  const payload = (error as AxiosError<{ error?: unknown; message?: unknown }>)
    ?.response?.data;
  if (typeof payload?.error === "string") return payload.error;
  if (typeof payload?.message === "string") return payload.message;
  return error instanceof Error ? error.message : "Request failed.";
};

const validSequence = (value: number): boolean => Number.isSafeInteger(value) && value > 0;
const validTimestamp = (value: number): boolean => Number.isInteger(value) && value > 0 &&
  Number.isFinite(new Date(value / 1e6).getTime());

const verifiedState = (value: unknown): LegacyReferralRetirementState => {
  const state = value as LegacyReferralRetirementState | undefined;
  const retirement = state?.retirement;
  const fees = state?.standard_perp_fee_reset;
  if (
    state?.claim_deadline_unix !== LEGACY_REFERRAL_CLAIM_DEADLINE ||
    state.target_account_type !== "STANDARD" ||
    state.perpetual_taker_bps !== "6.5" ||
    state.perpetual_maker_bps !== "3.5" ||
    (state.preflight_error !== null && typeof state.preflight_error !== "string") ||
    (retirement !== null && (!retirement ||
      !validSequence(retirement.retired_seq) || !validTimestamp(retirement.retired_at_unix_nano) ||
      retirement.claim_deadline_unix !== LEGACY_REFERRAL_CLAIM_DEADLINE)) ||
    (fees !== null && (!fees || !validSequence(fees.seq) || !validTimestamp(fees.timestamp_unix_nano)))
  ) throw new Error("The backend did not return the expected referral retirement and STANDARD fee reset state.");
  return state;
};

export const legacyReferralCutoverComplete = (state?: LegacyReferralRetirementState): boolean =>
  Boolean(state?.retirement && state.standard_perp_fee_reset &&
    state.retirement.retired_seq === state.standard_perp_fee_reset.seq &&
    state.retirement.retired_at_unix_nano === state.standard_perp_fee_reset.timestamp_unix_nano);

// The staff session and server permissions authorize the command. Completion
// requires the authoritative GET to record both checkpoints at the same event.
export const useLegacyReferralRetirement = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const endpoint = ADMIN_API_URL("legacy-referral-retirement");
  const swr = useSWR<LegacyReferralRetirementState, AxiosError | Error>(
    tokenId ? [endpoint, tokenId] : null,
    async () => verifiedState((await axios.get(endpoint, queryConfig())).data),
    { refreshInterval: 5000, shouldRetryOnError: false }
  );
  const busyRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [awaitingCheckpoint, setAwaitingCheckpoint] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const complete = legacyReferralCutoverComplete(swr.data);

  useEffect(() => {
    if (complete) setAwaitingCheckpoint(false);
  }, [complete]);

  const retireReferrals = useCallback(async () => {
    if (!tokenId || busyRef.current || awaitingCheckpoint || swr.error || !swr.data ||
        swr.data.preflight_error !== null || swr.data.retirement !== null ||
        swr.data.standard_perp_fee_reset !== null) return;
    busyRef.current = true;
    setSubmitting(true);
    setSubmissionError(undefined);
    try {
      const response = await axios.post<{ success?: boolean }>(endpoint, {
        confirm: "RETIRE_LEGACY_REFERRALS",
        claim_deadline_unix: LEGACY_REFERRAL_CLAIM_DEADLINE,
      }, queryConfig());
      setAwaitingCheckpoint(true);
      if (response.data?.success !== true) {
        throw new Error("The backend response did not confirm acceptance. Refresh the cutover state before taking further action.");
      }
      await swr.mutate();
    } catch (error) {
      // A lost response or a proxy/server error can follow acceptance. Never
      // automatically retry an uncertain command or infer success from POST.
      if (axios.isAxiosError(error) && (!error.response || error.response.status >= 500)) {
        setAwaitingCheckpoint(true);
      }
      setSubmissionError(legacyReferralRetirementError(error));
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  }, [tokenId, awaitingCheckpoint, swr, endpoint, queryConfig]);

  return { ...swr, signedIn: Boolean(tokenId), submitting, awaitingCheckpoint, submissionError, retireReferrals, complete };
};
